import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { swarmReducer } from "@/lib/swarmReducer";
import { INITIAL_SWARM_STATE, TOOL_TO_AGENT } from "@/lib/swarmTypes";
import { streamSwarmDirect, AgentCoreAuthError, AgentCoreError } from "@/lib/agentcore";
import type { AgentKey, SwarmAction, ThreatDimension, ThreatLevel, ThreatScore } from "@/lib/swarmTypes";
import type { AguiEvent, Viewer } from "@/lib/agentcore";

// ── Defensive parsing for synthesis-layer events ───────────────────────────────
// The threat/battlecard events come from a parallel backend; a malformed payload
// must degrade gracefully (drop the event) rather than throw inside the stream.

const THREAT_LEVELS: ThreatLevel[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function clampPct(v: unknown): number {
  return clampInt(v, 0, 100, 0);
}

function asStringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
}

function parseDimensions(v: unknown): ThreatDimension[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((d): ThreatDimension | null => {
      if (!d || typeof d !== "object") return null;
      const o = d as Record<string, unknown>;
      const label = typeof o.label === "string" ? o.label : typeof o.key === "string" ? o.key : "";
      if (!label) return null;
      const max = clampInt(o.max, 1, 100, 10);
      return {
        key: typeof o.key === "string" ? o.key : label,
        label,
        score: clampInt(o.score, 0, max, 0),
        max,
        note: typeof o.note === "string" ? o.note : "",
      };
    })
    .filter((d): d is ThreatDimension => d !== null);
}

function parseThreatScore(event: AguiEvent): ThreatScore | null {
  const competitor = typeof event.competitor === "string" ? event.competitor : "";
  const rawLevel = typeof event.level === "string" ? (event.level.toUpperCase() as ThreatLevel) : null;
  const level: ThreatLevel = rawLevel && THREAT_LEVELS.includes(rawLevel) ? rawLevel : "MEDIUM";
  // Require at least one substantive field so an empty stub never renders a card.
  const hasContent =
    typeof event.score === "number" ||
    typeof event.rationale === "string" ||
    Array.isArray(event.dimensions);
  if (!hasContent) return null;
  return {
    competitor,
    score: clampInt(event.score, 1, 10, 1),
    level,
    rationale: typeof event.rationale === "string" ? event.rationale : "",
    whyItMattersToUs: typeof event.whyItMattersToUs === "string" ? event.whyItMattersToUs : "",
    confidence: clampPct(event.confidence),
    dimensions: parseDimensions(event.dimensions),
    topThreats: asStringList(event.topThreats),
    counterMoves: asStringList(event.counterMoves),
    sources: asStringList(event.sources),
  };
}

/**
 * Connection health, surfaced ALONGSIDE (never inside) the swarm state so the
 * AG-UI/SSE payload + reducer contract stays untouched. Drives the friendly,
 * non-scary reconnect messaging on mobile.
 *   online       - normal
 *   offline      - the device reported `offline` (navigator.onLine === false)
 *   reconnecting - a run dropped its connection mid-flight; we are trying to
 *                  pick it back up (auto or on the user's tap)
 */
export type ConnectionState = "online" | "offline" | "reconnecting";

/**
 * A friendly "you have reached today's allowance" signal, surfaced ALONGSIDE
 * (never inside) the swarm reducer so the AG-UI/SSE payload + reducer contract
 * stays untouched. Set when the backend emits a RATE_LIMITED event mid-stream;
 * the run then finishes cleanly (RUN_FINISHED, HTTP 200) so this is NOT an error.
 *   scope    - "day" hit their own daily allowance; "month" hit their own monthly
 *              allowance; "global" = the shared demo budget is spent for the day;
 *              "suspended" = account paused.
 *   limit    - the allowance that was reached.
 *   used     - how many analyses had been run.
 *   resetsAt - ISO 8601 of when the allowance refreshes (relative-time it in UI).
 *   message  - the backend's friendly copy (the UI may prefer its own wording).
 */
export interface LimitInfo {
  scope: "day" | "month" | "global" | "suspended";
  limit: number;
  used: number;
  resetsAt: string;
  message: string;
}

const MAX_AUTO_RETRIES = 2;

export function useSwarmStream(
  idToken: string,
  sessionId: string,
  onAuthError: () => void,
) {
  const [state, dispatch] = useReducer(swarmReducer, INITIAL_SWARM_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<number>(0);

  // ── Mobile network resilience (additive; happy path unchanged) ──────────────
  // Backgrounding a mobile browser mid-run kills the fetch/SSE socket. Rather
  // than show a scary "Network error", we remember what was running and try to
  // RECOVER on return-to-foreground / regaining connection by re-issuing the
  // SAME run (the backend cache returns it fast). All state here is hook-local.
  const [connection, setConnection] = useState<ConnectionState>(
    typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online",
  );
  // True when an in-flight run lost its connection and has NOT yet recovered -
  // gates the friendly reconnecting banner + the manual Resume/Retry button.
  const [recoverable, setRecoverable] = useState(false);
  // Set when the backend gently stops a run because a daily allowance was
  // reached (RATE_LIMITED). Hook-local so the swarm reducer/contract is untouched.
  const [limitInfo, setLimitInfo] = useState<LimitInfo | null>(null);
  // The params of the run we should re-issue to recover. Held in a ref so the
  // visibility/online listeners (registered once) always see the latest.
  const lastRunRef = useRef<{ competitor: string; depth: "quick" | "deep"; viewer: Viewer } | null>(null);
  // True only while a run is actively in flight (between start and settle).
  const inFlightRef = useRef(false);
  const autoRetriesRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // rAF batching: accumulate actions between frames, flush once per animation frame.
  // Prevents re-render tsunami during high-frequency TEXT_MESSAGE_CONTENT events.
  const pendingRef = useRef<SwarmAction[]>([]);
  const rafRef = useRef<number | null>(null);

  const flush = useCallback(() => {
    const batch = pendingRef.current.splice(0);
    for (const action of batch) dispatch(action);
    rafRef.current = null;
  }, []);

  const enqueue = useCallback((action: SwarmAction) => {
    pendingRef.current.push(action);
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(flush);
    }
  }, [flush]);

  const mapEvent = useCallback((event: AguiEvent): SwarmAction | null => {
    switch (event.type) {
      case "RUN_STARTED":
        return null; // RUN_STARTED is handled synchronously before the stream

      case "RUN_FINISHED":
        return { type: "RUN_FINISHED", elapsedMs: Date.now() - startedAtRef.current };

      case "RUN_ERROR":
        return { type: "RUN_ERROR", message: event.message ?? event.error ?? "Unknown error from agent swarm" };

      case "TOOL_CALL_START": {
        // The swarm path emits MCP tool names (browser_navigate, fetch, …) that
        // are NOT in TOOL_TO_AGENT, so the backend attaches an agentKey routing
        // hint. Pass it through (validated) so the reducer can place the card on
        // the right agent instead of dropping it.
        const hintKey = event.agentKey as AgentKey | undefined;
        const knownAgents: AgentKey[] = ["video", "document", "web", "news"];
        return {
          type: "TOOL_CALL_STARTED",
          toolCallId: event.toolCallId ?? "",
          toolCallName: event.toolCallName ?? "",
          agentKey: hintKey && knownAgents.includes(hintKey) ? hintKey : undefined,
        };
      }

      case "TOOL_CALL_END":
        return { type: "TOOL_CALL_ENDED", toolCallId: event.toolCallId ?? "" };

      case "TOOL_CALL_ARGS":
        return { type: "TOOL_CALL_ARGS_CHUNK", toolCallId: event.toolCallId ?? "", delta: event.delta ?? "" };

      case "AGENT_DONE": {
        const key = event.agentKey as AgentKey | undefined;
        const knownAgents: AgentKey[] = ["video", "document", "web", "news"];
        if (!key || !knownAgents.includes(key)) return null;
        return { type: "AGENT_DONE", agentKey: key };
      }

      case "THREAT_SCORE": {
        // Synthesis-layer event - fused threat assessment. Validate defensively:
        // a malformed payload must never crash the stream, just be dropped.
        const threat = parseThreatScore(event);
        return threat ? { type: "THREAT_SCORE_SET", threat } : null;
      }

      case "BATTLECARD": {
        const markdown = typeof event.markdown === "string" ? event.markdown : "";
        if (!markdown.trim()) return null;
        return {
          type: "BATTLECARD_SET",
          battlecard: { markdown, confidence: clampPct(event.confidence) },
        };
      }

      case "AGENT_TEXT_DELTA": {
        const key = event.agentKey as AgentKey | undefined;
        const knownAgents: AgentKey[] = ["video", "document", "web", "news"];
        if (!key || !knownAgents.includes(key)) return null;
        return {
          type: "AGENT_TEXT_CHUNK",
          agentKey: key,
          delta: event.delta ?? "",
          kind: (event.kind === "reasoning" ? "reasoning" : "text") as "text" | "reasoning",
        };
      }

      case "STEP_STARTED": {
        // stepName encodes agentKey: "video:analyzing" or bare tool name for root steps
        const stepName = event.stepName ?? "";
        const [maybeKey, ...rest] = stepName.split(":");
        const agentKey = maybeKey as AgentKey;
        const knownAgents: AgentKey[] = ["video", "document", "web", "news"];
        if (knownAgents.includes(agentKey)) {
          const text = rest.join(":") || stepName;
          return { type: "AGENT_STEP", agentKey, text, kind: "step" };
        }
        // Root-level step or tool-named step - try to derive agent from TOOL_TO_AGENT
        const toolAgent = TOOL_TO_AGENT[stepName];
        if (toolAgent) {
          return { type: "AGENT_STEP", agentKey: toolAgent, text: stepName.replace(/_/g, " "), kind: "step" };
        }
        return null;
      }

      case "TEXT_MESSAGE_START":
      case "TEXT_MESSAGE_END":
        // Message boundary events from the structured AG-UI text stream.
        // They carry no renderable delta; the START/END just frame the
        // TEXT_MESSAGE_CONTENT chunks. Nothing to dispatch.
        return null;

      case "TEXT_MESSAGE_CONTENT": {
        const delta = event.delta ?? event.data;
        if (!delta) return null;
        // A structured per-agent text message (carries agentKey) streams into
        // that agent's card body; a bare message is the final synthesis.
        const key = event.agentKey as AgentKey | undefined;
        const knownAgents: AgentKey[] = ["video", "document", "web", "news"];
        if (key && knownAgents.includes(key)) {
          return { type: "AGENT_TEXT_CHUNK", agentKey: key, delta, kind: "text" };
        }
        return { type: "TEXT_CHUNK", delta };
      }

      default:
        if (import.meta.env.DEV) {
          console.log("[swarm] event:", event);
        }
        return null;
    }
  }, []);

  // Core stream runner. `isRecovery` re-issues a previously-dropped run; the
  // backend cache returns it quickly so recovery feels instant. forceRefresh is
  // never used on recovery (we WANT the cached result).
  const runStream = useCallback(
    async (competitor: string, depth: "quick" | "deep", forceRefresh: boolean, viewer: Viewer) => {
      // Cancel any in-flight stream
      abortRef.current?.abort();
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      pendingRef.current = [];

      const controller = new AbortController();
      abortRef.current = controller;
      startedAtRef.current = Date.now();

      // Generate a fresh sessionId for every run so AgentCore never sees two
      // concurrent in-flight requests on the same session.
      const runSessionId = `${sessionId}-${crypto.randomUUID()}`;

      inFlightRef.current = true;
      // Dispatch RUN_STARTED synchronously so the UI transitions immediately
      dispatch({ type: "RUN_STARTED" });

      try {
        await streamSwarmDirect(
          { competitor, depth, idToken, sessionId: runSessionId, signal: controller.signal, forceRefresh, viewer },
          (event) => {
            // RATE_LIMITED is a soft stop, not an error: capture it as hook-local
            // state (so the reducer contract is untouched) and let the trailing
            // RUN_FINISHED settle the run cleanly. We deliberately do NOT map it
            // to a SwarmAction, so no scary RUN_ERROR banner ever shows.
            if (event.type === "RATE_LIMITED") {
              // Map the backend scope through, defaulting to the per-user daily ("day")
              // bucket for any unexpected value so the UI always shows friendly copy.
              const scope: LimitInfo["scope"] =
                event.scope === "global" || event.scope === "suspended" || event.scope === "month"
                  ? event.scope
                  : "day";
              setLimitInfo({
                scope,
                limit: typeof event.limit === "number" ? event.limit : Number(event.limit) || 0,
                used: typeof event.used === "number" ? event.used : Number(event.used) || 0,
                resetsAt: typeof event.resetsAt === "string" ? event.resetsAt : "",
                message: typeof event.message === "string" ? event.message : "",
              });
              return;
            }
            const action = mapEvent(event);
            if (action) enqueue(action);
          },
        );
        // Clean finish: clear any lingering reconnect state.
        inFlightRef.current = false;
        autoRetriesRef.current = 0;
        setRecoverable(false);
        setConnection(navigator.onLine === false ? "offline" : "online");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof AgentCoreAuthError) { inFlightRef.current = false; onAuthError(); return; }

        // A connection-level failure is treated as a RECOVERABLE drop rather than
        // a hard error: keep the run "in flight", show the calm reconnecting
        // state, and auto-retry a couple of times with backoff.
        //
        // Discriminator: a genuine backend failure surfaces EITHER as a thrown
        // AgentCoreError carrying an HTTP statusCode (initial non-OK response), OR
        // as a RUN_ERROR event (dispatched via mapEvent, never thrown here).
        // EVERYTHING else thrown - the network-catch AgentCoreError (no
        // statusCode), or a raw TypeError/DOMException from reader.read() when the
        // socket drops mid-stream on mobile - is a transport drop we can recover.
        const isHttpError = err instanceof AgentCoreError && err.statusCode !== undefined;
        if (!isHttpError) {
          setConnection(navigator.onLine === false ? "offline" : "reconnecting");
          setRecoverable(true);
          // Auto-retry with backoff, but only while online and under the cap.
          if (autoRetriesRef.current < MAX_AUTO_RETRIES && navigator.onLine !== false) {
            const attempt = autoRetriesRef.current + 1;
            autoRetriesRef.current = attempt;
            const delay = 1200 * attempt; // 1.2s, 2.4s
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            retryTimerRef.current = setTimeout(() => {
              if (inFlightRef.current) void runStream(competitor, depth, false, viewer);
            }, delay);
          }
          // Stay in-flight; do NOT dispatch RUN_ERROR (no scary banner).
          return;
        }

        // A genuine backend / HTTP error - surface it as before.
        inFlightRef.current = false;
        dispatch({ type: "RUN_ERROR", message: err.message });
      }
    },
    [idToken, sessionId, onAuthError, mapEvent, enqueue],
  );

  const start = useCallback(
    async (competitor: string, depth: "quick" | "deep", forceRefresh = false, viewer: Viewer = {}) => {
      lastRunRef.current = { competitor, depth, viewer };
      autoRetriesRef.current = 0;
      setRecoverable(false);
      setLimitInfo(null); // a fresh run clears any prior allowance notice
      await runStream(competitor, depth, forceRefresh, viewer);
    },
    [runStream],
  );

  // Manual + automatic recovery: re-issue the last run (cache-backed). Used by
  // the Resume/Retry button and by the visibility/online listeners below.
  const resume = useCallback(() => {
    const last = lastRunRef.current;
    if (!last) return;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    autoRetriesRef.current = 0;
    setConnection(navigator.onLine === false ? "offline" : "reconnecting");
    void runStream(last.competitor, last.depth, false, last.viewer);
  }, [runStream]);

  const stop = useCallback(() => {
    inFlightRef.current = false;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    setRecoverable(false);
    abortRef.current?.abort();
    dispatch({ type: "RUN_ERROR", message: "Cancelled by user" });
  }, []);

  const reset = useCallback(() => {
    inFlightRef.current = false;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    lastRunRef.current = null;
    autoRetriesRef.current = 0;
    setRecoverable(false);
    setLimitInfo(null);
    abortRef.current?.abort();
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    pendingRef.current = [];
    dispatch({ type: "RESET" });
  }, []);

  // ── Recover on return-to-foreground / regaining connection ──────────────────
  // Registered once. If a run is still in flight (it dropped its socket while the
  // tab was backgrounded or the device went offline), re-issue it when the tab
  // becomes visible again or `online` fires. Cache-backed, so it returns fast.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && inFlightRef.current && recoverable) {
        resume();
      }
    };
    const onOnline = () => {
      setConnection("online");
      if (inFlightRef.current && recoverable) resume();
    };
    const onOffline = () => {
      if (inFlightRef.current) setConnection("offline");
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [recoverable, resume]);

  // Abort any in-flight stream + cancel the rAF batch when the consumer unmounts
  // (e.g. navigating away from the analyze route mid-run). Pure resource cleanup
  // - it does NOT dispatch, so it never touches the dying reducer, and it leaves
  // the AG-UI/SSE event handling, payloads, and auth entirely unchanged.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    };
  }, []);

  const approve = useCallback(() => {
    dispatch({ type: "APPROVE" });
  }, []);

  const setAwaitingApproval = useCallback(() => {
    dispatch({ type: "SET_AWAITING_APPROVAL" });
  }, []);

  return { state, start, stop, reset, approve, setAwaitingApproval, connection, recoverable, resume, limitInfo };
}
